import { useEffect, useMemo, useState } from 'react';
import { create } from 'zustand';
import { MODAL, RELEASE } from './content';
import { Icon } from '@landing/icons/Icons';
import { QrTile, triggerApkDownload } from './install';
import { u } from '@landing/base';
import './install-modal.css';

/*
 * Модальное окно установки. Сделано 1-в-1 по прототипу.
 *
 * Три способа, не больше. Определение устройства поднимает подходящий
 * наверх и подсвечивает его, но ничего не прячет: человек с айфона должен
 * видеть андроидный путь целиком, чтобы переслать его другому.
 *
 * QR только на широком экране — с телефона он бессмыслен.
 *
 * Закрывается крестиком, кликом вне окна и клавишей Esc. Фон затемняется и
 * размывается — так требует прототип.
 */

export const useInstallModal = create((set) => ({
  open: false,
  openModal: () => set({ open: true }),
  closeModal: () => set({ open: false }),
}));

function detectPlatform() {
  if (typeof navigator === 'undefined') return 'web';
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1))
    return 'ios';
  return 'web';
}

export function InstallModal() {
  const open = useInstallModal((s) => s.open);
  const close = useInstallModal((s) => s.closeModal);
  const [platform] = useState(detectPlatform);
  const [pending, setPending] = useState(null);
  const [wide, setWide] = useState(
    () =>
      typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const on = (e) => setWide(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    // Страница под окном не должна прокручиваться вместе с ним.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  const methods = useMemo(() => {
    const list = [...MODAL.methods];
    list.sort((a, b) => (a.id === platform ? -1 : b.id === platform ? 1 : 0));
    return list;
  }, [platform]);

  if (!open) return null;

  const go = (m) => (e) => {
    if (m.id === 'android') {
      e.preventDefault();
      // Состояние загрузки живёт до самого перехода: без него нажатие
      // выглядит как «ничего не произошло», пока браузер думает.
      setPending(m.id);
      triggerApkDownload();
      window.setTimeout(() => window.location.assign(u('/android?dl=1')), 400);
      return;
    }
    if (m.id === 'web') {
      e.preventDefault();
      setPending(m.id);
      window.location.assign(u(m.href));
    }
  };

  return (
    <div className="im" role="presentation">
      <button
        type="button"
        className="im__scrim"
        aria-label="Закрыть"
        onClick={close}
      />

      <div className="im__win" role="dialog" aria-modal="true" aria-label={MODAL.title}>
        <header className="im__head">
          <div>
            <h2 className="im__title">{MODAL.title}</h2>
            <p className="im__lead">{MODAL.lead}</p>
          </div>
          <button
            type="button"
            className="im__close"
            onClick={close}
            aria-label="Закрыть"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <ul className="im__list">
          {methods.map((m) => (
            <li
              key={m.id}
              className={`im__row ${m.id === platform ? 'is-suggested' : ''}`}
            >
              <span className="im__icon">
                <Icon name={m.icon} />
              </span>

              <div className="im__text">
                <p className="im__name">{m.name}</p>
                <p className="im__hint">{m.text}</p>
                {m.link && (
                  <a className="im__link" href={u(m.link.href)}>
                    {m.link.label} →
                  </a>
                )}
              </div>

              <a
                className={`im__action ${pending === m.id ? 'is-loading' : ''}`}
                href={m.id === 'android' ? m.href : u(m.href)}
                onClick={go(m)}
                {...(m.id === 'android'
                  ? { download: `aura-${RELEASE.version}.apk` }
                  : {})}
              >
                {pending === m.id ? (
                  <span className="im__spinner" />
                ) : (
                  <span className="im__actionLabel">{m.action}</span>
                )}
              </a>
            </li>
          ))}
        </ul>

        {wide && (
          <div className="im__qr">
            <span className="im__icon">
              <Icon name="qr" />
            </span>
            <div className="im__text">
              <p className="im__name">{MODAL.qr.title}</p>
              <p className="im__hint">{MODAL.qr.text}</p>
            </div>
            <QrTile url={MODAL.qr.url} size={96} fallback={<Icon name="qr" />} />
          </div>
        )}

        <ul className="im__assurances">
          {MODAL.assurances.map((a) => (
            <li key={a.title}>
              <Icon name={a.icon} />
              <span>
                <b>{a.title}</b>
                {a.text}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
